import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthModal from './components/AuthModal';
import DeleteAccountModal from './components/DeleteAccountModal';
import Dashboard from './components/Dashboard';
import HomePage from './components/HomePage';
import LessonScreen from './components/LessonScreen';
import LessonSummary from './components/LessonSummary';
import MiniCheckpointScreen from './components/MiniCheckpointScreen';
import MiniCheckpointSummary from './components/MiniCheckpointSummary';
import LegalPage from './components/LegalPage';
import SaveErrorBanner from './components/SaveErrorBanner';
import SprintSummary from './components/SprintSummary';
import ThemeToggle from './components/ThemeToggle';
import TrinPassedScreen from './components/TrinPassedScreen';
import TrinProofScreen from './components/TrinProofScreen';
import UserSwitcher from './components/UserSwitcher';
import WordSprintScreen from './components/WordSprintScreen';
import BaselineTranscriptionScreen from './components/BaselineTranscriptionScreen';
import BaselineCompositionScreen from './components/BaselineCompositionScreen';
import SkrivelegScreen from './components/SkrivelegScreen';
import Story3Screen from './components/Story3Screen';
import WpmCheckInScreen from './components/WpmCheckInScreen';
import WpmCheckInSummary from './components/WpmCheckInSummary';
import OnboardingFlow, {
  type OnboardingPhase,
} from './components/OnboardingFlow';
import {
  needsBaselineTranscription,
  needsOnboarding,
} from './lib/baseline';
import { goalsFromArchetype } from './lib/goals';
import { getCheckpoint, getMiniCheckpoint, getTrin } from './data/curriculum';
import { firstDictationRoundIndex } from './lib/lessonGenerator';
import {
  canStartCheckpoint,
  canStartMiniCheckpoint,
  canStartTrin,
  getActiveTrin,
  getNextTrin,
  hasPassedTrinProof,
  isCheckpointAccessible,
  isMiniCheckpointAccessible,
  isTrinAccessible,
} from './lib/curriculum';
import { PROGRESSION } from './lib/featureFlags';
import { getPersistence } from './lib/persistence';
import {
  buildDictationTrin,
  buildStoryTrin,
  buildStory2Trin,
  buildBonusEmailTrin,
  buildBonusTalTrin,
  buildMetronomeTrin,
  buildShadowTrin,
  buildWeakSpotsTrin,
  buildPunctuationPracticeTrin,
  buildCustomPracticeShellTrin,
  buildMentalImageryTrin,
  BONUS_EMAIL_TRIN_ID,
  BONUS_TAL_TRIN_ID,
  CUSTOM_PRACTICE_TRIN_ID,
  DICTATION_TRIN_ID,
  PUNCTUATION_PRACTICE_TRIN_ID,
  STORY_TRIN_ID,
  STORY2_TRIN_ID,
  isExtraTrinId,
  METRONOME_TRIN_ID,
  SHADOW_TRIN_ID,
  WEAK_SPOTS_TRIN_ID,
  MENTAL_IMAGERY_TRIN_ID,
} from './lib/extraRounds';
import { buildWarmupTrin, WARMUP_TRIN_ID } from './lib/warmup';
import {
  buildFlowPracticeShellTrin,
  FLOW_PRACTICE_TRIN_ID,
  isFlowPracticeEligible,
} from './lib/flowPractice';
import { updateMetronomeSurvivalRecord } from './lib/metronomeSurvival';
import {
  addCheckpointResult,
  addLessonResult,
  addMiniCheckpointResult,
  addTrinProofResult,
  addWpmCheckIn,
  advanceAfterCheckpoint,
  completeOnboarding,
  createUser,
  defaultPreferences,
  devJumpToTrin,
  emptyState,
  getActiveUser,
  removeUser,
  setActiveUser,
  setTheme,
  setUserBaseline,
  setUserGoals,
  setUserPreferences,
  upsertUser,
} from './lib/storage';
import { isWpmCheckInEligible } from './lib/wpmCheckIn';
import { aggregateRounds, getTrinRecord } from './lib/stats';
import type {
  AppState,
  Badge,
  BaselineSnapshot,
  GoalArchetypeId,
  KeyboardLayoutId,
  LessonResult,
  MiniCheckpointResult,
  RoundResult,
  Screen,
  SprintResult,
  Trin,
  UserGoals,
  UserPreferencesPatch,
  WpmCheckIn,
} from './types';

export default function App() {
  const [state, setState] = useState<AppState>(() => emptyState());
  const [screen, setScreen] = useState<Screen>('home');
  const legalReturnScreen = useRef<Screen>('home');
  const [lastResult, setLastResult] = useState<LessonResult | null>(null);
  const [lastSprint, setLastSprint] = useState<SprintResult | null>(null);
  const [lessonTrinId, setLessonTrinId] = useState<string | null>(null);
  const [lessonStartRoundIndex, setLessonStartRoundIndex] = useState<
    number | null
  >(null);
  const [warmupTrin, setWarmupTrin] = useState<Trin | null>(null);
  const [playCheckpointId, setPlayCheckpointId] = useState<string | null>(null);
  const [playMiniCheckpointId, setPlayMiniCheckpointId] = useState<
    string | null
  >(null);
  const [lastMiniCheckpoint, setLastMiniCheckpoint] =
    useState<MiniCheckpointResult | null>(null);
  const [proofTrinId, setProofTrinId] = useState<string | null>(null);
  const [lastProofAccuracy, setLastProofAccuracy] = useState<number | null>(
    null,
  );
  const [proofFailed, setProofFailed] = useState(false);
  const [miniCheckpointSessionKey, setMiniCheckpointSessionKey] = useState(0);
  const [lessonRecordBeat, setLessonRecordBeat] = useState(false);
  const [metronomeSurvivalRecordBeat, setMetronomeSurvivalRecordBeat] =
    useState(false);
  const [newBadges, setNewBadges] = useState<Badge[]>([]);
  const [sprintSessionKey, setSprintSessionKey] = useState(0);
  const [storySessionKey, setStorySessionKey] = useState(0);
  const [story2SessionKey, setStory2SessionKey] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [onboardingPhase, setOnboardingPhase] =
    useState<OnboardingPhase>('welcome');
  const [baselineRetest, setBaselineRetest] = useState<
    'transcription' | 'composition' | null
  >(null);
  const [wpmCheckInSummary, setWpmCheckInSummary] = useState<{
    checkIn: WpmCheckIn;
    previous?: WpmCheckIn;
    baseline?: BaselineSnapshot;
  } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistence = useMemo(() => getPersistence(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await persistence.hydrate();
      if (!cancelled) {
        setState(loaded);
        setAuthenticated(persistence.isAuthenticated());
        setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [persistence]);

  useEffect(() => {
    if (!hydrated) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void persistence.save(state).then((ok) => {
        setSaveError(!ok);
      });
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [state, hydrated, persistence]);

  const retrySave = useCallback(() => {
    void persistence.save(state).then((ok) => setSaveError(!ok));
  }, [persistence, state]);

  useEffect(() => {
    if (!hydrated) return;
    const root = document.documentElement;
    if (state.theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [state.theme, hydrated]);

  const activeUser = useMemo(() => getActiveUser(state), [state]);

  useEffect(() => {
    if (!hydrated || !activeUser) return;
    if (!needsBaselineTranscription(activeUser)) return;
    if (
      screen !== 'home' &&
      screen !== 'dashboard' &&
      screen !== 'users'
    ) {
      return;
    }
    setBaselineRetest('transcription');
    setScreen('baselineTranscription');
  }, [hydrated, activeUser, screen]);

  const playTrin = useMemo(() => {
    if (!activeUser) return null;
    if (warmupTrin) return warmupTrin;
    const id = lessonTrinId ?? activeUser.currentTrinId;
    return getTrin(id) ?? getActiveTrin(activeUser);
  }, [activeUser, lessonTrinId, warmupTrin]);

  const playCheckpoint = useMemo(() => {
    if (!playCheckpointId) return null;
    return getCheckpoint(playCheckpointId) ?? null;
  }, [playCheckpointId]);

  const playMiniCheckpoint = useMemo(() => {
    if (!playMiniCheckpointId) return null;
    return getMiniCheckpoint(playMiniCheckpointId) ?? null;
  }, [playMiniCheckpointId]);

  const proofTrin = useMemo(() => {
    if (!proofTrinId) return null;
    return getTrin(proofTrinId) ?? null;
  }, [proofTrinId]);

  const handleToggleTheme = useCallback(() => {
    setState((s) => setTheme(s, s.theme === 'dark' ? 'light' : 'dark'));
  }, []);

  const resumeOnboardingPhase = useCallback(
    (user: NonNullable<typeof activeUser>): OnboardingPhase => {
      if (!user.baselineTranscription) return 'welcome';
      if (!user.baselineComposition) return 'offerEveryday';
      return 'goals';
    },
    [],
  );

  const navigateForUser = useCallback(
    (user: NonNullable<typeof activeUser>) => {
      if (needsOnboarding(user)) {
        setOnboardingPhase(resumeOnboardingPhase(user));
        setScreen('onboarding');
      } else if (needsBaselineTranscription(user)) {
        setBaselineRetest('transcription');
        setScreen('baselineTranscription');
      } else {
        setScreen('dashboard');
      }
    },
    [resumeOnboardingPhase],
  );

  const handleGoHome = useCallback(() => {
    setLastResult(null);
    setLastSprint(null);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setWarmupTrin(null);
    setPlayCheckpointId(null);
    setPlayMiniCheckpointId(null);
    setLastMiniCheckpoint(null);
    setProofTrinId(null);
    setLastProofAccuracy(null);
    setProofFailed(false);
    setLessonRecordBeat(false);
    setMetronomeSurvivalRecordBeat(false);
    setNewBadges([]);
    setBaselineRetest(null);
    setWpmCheckInSummary(null);
    if (activeUser) {
      navigateForUser(activeUser);
    } else {
      setScreen('home');
    }
  }, [activeUser, navigateForUser]);

  const openLegal = useCallback((returnTo: Screen) => {
    legalReturnScreen.current = returnTo;
    setScreen('privacy');
  }, []);

  const handleHomeStart = () => {
    if (activeUser) {
      navigateForUser(activeUser);
    } else if (state.users.length > 0) {
      setScreen('users');
    } else {
      setScreen('users');
    }
  };

  const handleCreateUser = (name: string) => {
    const user = createUser(name);
    let next = upsertUser(state, user);
    next = setActiveUser(next, user.id);
    setState(next);
    setOnboardingPhase('welcome');
    setScreen('onboarding');
  };

  const handleSelectUser = (id: string) => {
    const next = setActiveUser(state, id);
    setState(next);
    const user = next.users.find((u) => u.id === id);
    if (user) navigateForUser(user);
  };

  const handleKeyboardLayout = (layout: KeyboardLayoutId) => {
    if (!activeUser) return;
    setState(upsertUser(state, { ...activeUser, keyboardLayout: layout }));
  };

  const handlePreferencesChange = (patch: UserPreferencesPatch) => {
    if (!activeUser) return;
    setState(upsertUser(state, setUserPreferences(activeUser, patch)));
  };

  const handleGoalsChange = (patch: Partial<UserGoals>) => {
    if (!activeUser) return;
    const merged = {
      ...(activeUser.goals ?? goalsFromArchetype('hverdagstastatur')),
      ...patch,
    };
    setState(upsertUser(state, setUserGoals(activeUser, merged)));
  };

  const handleBaselineTranscriptionFinish = (snapshot: BaselineSnapshot) => {
    if (!activeUser) return;
    const updated = setUserBaseline(activeUser, snapshot);
    setState(upsertUser(state, updated));
    if (baselineRetest || activeUser.onboardingCompletedAt) {
      setBaselineRetest(null);
      setScreen('dashboard');
      return;
    }
    setOnboardingPhase('offerEveryday');
    setScreen('onboarding');
  };

  const handleBaselineCompositionFinish = (snapshot: BaselineSnapshot) => {
    if (!activeUser) return;
    const updated = setUserBaseline(activeUser, snapshot);
    setState(upsertUser(state, updated));
    if (baselineRetest) {
      setBaselineRetest(null);
      setScreen('dashboard');
      return;
    }
    setOnboardingPhase('summary');
    setScreen('onboarding');
  };

  const handleCompleteOnboarding = (archetypeId: GoalArchetypeId) => {
    if (!activeUser) return;
    const updated = completeOnboarding(
      activeUser,
      goalsFromArchetype(archetypeId),
    );
    setState(upsertUser(state, updated));
    setScreen('dashboard');
  };

  const handleRetestBaseline = (kind: 'transcription' | 'composition') => {
    setBaselineRetest(kind);
    setScreen(
      kind === 'transcription' ? 'baselineTranscription' : 'baselineComposition',
    );
  };

  const handleStartWpmCheckIn = () => {
    if (!activeUser || !isWpmCheckInEligible(activeUser)) return;
    setScreen('wpmCheckIn');
  };

  const handleWpmCheckInFinish = (checkIn: WpmCheckIn) => {
    if (!activeUser) return;
    const previous = activeUser.wpmCheckIns?.[0];
    const baseline = activeUser.baselineTranscription;
    const updated = addWpmCheckIn(activeUser, checkIn);
    setState(upsertUser(state, updated));
    setWpmCheckInSummary({ checkIn, previous, baseline });
    setScreen('wpmCheckInSummary');
  };

  const handleWpmCheckInSummaryContinue = () => {
    setWpmCheckInSummary(null);
    setScreen('dashboard');
  };

  const handleCancelWpmCheckIn = () => {
    setScreen('dashboard');
  };

  const handleStartOnboardingBaseline = () => {
    setBaselineRetest(null);
    setScreen('baselineTranscription');
  };

  const handleAuthSuccess = async () => {
    const loaded = await persistence.hydrate();
    setState(loaded);
    setAuthenticated(persistence.isAuthenticated());
    setAuthOpen(false);
  };

  const handleLogout = async () => {
    await persistence.logout();
    const loaded = await persistence.hydrate();
    setState(loaded);
    setAuthenticated(false);
  };

  const handleDeleteUser = (userId: string) => {
    setState((s) => removeUser(s, userId));
  };

  const handleDeleteAccount = async (password: string) => {
    await persistence.deleteAccount(password);
    const loaded = await persistence.hydrate();
    setState(loaded);
    setAuthenticated(false);
    setDeleteAccountOpen(false);
    setScreen(loaded.users.length > 0 ? 'users' : 'home');
  };

  const handleStartTrin = useCallback(
    (trinId: string) => {
      if (!activeUser) return;
      const trin = getTrin(trinId);
      if (!trin || !canStartTrin(trin) || !isTrinAccessible(activeUser, trin)) {
        return;
      }
      setState((s) => {
        const u = getActiveUser(s);
        if (!u) return s;
        return upsertUser(s, { ...u, currentTrinId: trinId });
      });
      setLessonTrinId(trinId);
      setLessonStartRoundIndex(null);
      setWarmupTrin(null);
      setPlayCheckpointId(null);
      setLastResult(null);
      setLastSprint(null);
      setNewBadges([]);
      setScreen('lesson');
    },
    [activeUser],
  );

  const handleDevJump = useCallback(
    (trinId: string) => {
      if (!activeUser) return;
      const jumped = devJumpToTrin(activeUser, trinId);
      if (!jumped) return;
      setState(upsertUser(state, jumped));
    },
    [activeUser, state],
  );

  const handleDevJumpAndStart = useCallback(
    (trinId: string) => {
      if (!activeUser) return;
      const jumped = devJumpToTrin(activeUser, trinId);
      if (!jumped) return;
      const trin = getTrin(trinId);
      if (!trin || !canStartTrin(trin)) return;
      setState(upsertUser(state, jumped));
      setLessonTrinId(trinId);
      setLessonStartRoundIndex(null);
      setWarmupTrin(null);
      setPlayCheckpointId(null);
      setLastResult(null);
      setLastSprint(null);
      setNewBadges([]);
      setScreen('lesson');
    },
    [activeUser, state],
  );

  const handleDevJumpToDictation = useCallback(
    (trinId: string) => {
      if (!activeUser) return;
      const trin = getTrin(trinId);
      if (!trin || !canStartTrin(trin)) return;
      const dictIdx = firstDictationRoundIndex(trin);
      if (dictIdx < 0) return;
      const jumped = devJumpToTrin(activeUser, trinId);
      if (!jumped) return;
      setState(upsertUser(state, jumped));
      setLessonTrinId(trinId);
      setLessonStartRoundIndex(dictIdx);
      setWarmupTrin(null);
      setPlayCheckpointId(null);
      setLastResult(null);
      setLastSprint(null);
      setNewBadges([]);
      setScreen('lesson');
    },
    [activeUser, state],
  );

  const handleStartWarmup = useCallback(() => {
    if (!activeUser) return;
    const trin = buildWarmupTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartFlowPractice = useCallback(() => {
    if (!activeUser || !isFlowPracticeEligible(activeUser)) return;
    const trin = buildFlowPracticeShellTrin();
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartMetronome = useCallback(() => {
    if (!activeUser) return;
    const trin = buildMetronomeTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartShadow = useCallback(() => {
    if (!activeUser) return;
    const trin = buildShadowTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartDictation = useCallback(() => {
    if (!activeUser) return;
    const trin = buildDictationTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartStory = useCallback(() => {
    if (!activeUser) return;
    const trin = buildStoryTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartStory2 = useCallback(() => {
    if (!activeUser) return;
    const trin = buildStory2Trin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartSkriveleg = useCallback(() => {
    if (!activeUser) return;
    setWarmupTrin(null);
    setLessonTrinId(null);
    setScreen('skriveleg');
  }, [activeUser]);

  const handleFinishSkriveleg = useCallback(() => {
    setScreen('dashboard');
  }, []);

  const handleStartStory3 = useCallback(() => {
    if (!activeUser) return;
    setWarmupTrin(null);
    setLessonTrinId(null);
    setScreen('story3');
  }, [activeUser]);

  const handleFinishStory3 = useCallback(() => {
    setScreen('dashboard');
  }, []);

  const persistStoryLesson = useCallback(
    (rounds: RoundResult[], startedAt: string) => {
      if (!activeUser || !playTrin) return;
      const result = aggregateRounds(
        playTrin.id,
        playTrin.tierId,
        startedAt,
        rounds,
      );
      const before = activeUser;
      let after = addLessonResult(before, result, {
        skipProgression: true,
      });
      const storyRound = playTrin.rounds[0]?.presentation;
      const storyId =
        storyRound?.kind === 'story' ? storyRound.storyId : undefined;
      if (storyId) {
        after = {
          ...after,
          preferences: {
            ...(after.preferences ?? defaultPreferences()),
            lastStoryId: storyId,
          },
        };
      }
      const beforeIds = new Set(before.badges.map((b) => b.id));
      const fresh = after.badges.filter((b) => !beforeIds.has(b.id));
      setState(upsertUser(state, after));
      setNewBadges(fresh);
      setWarmupTrin(null);
      setLessonStartRoundIndex(null);
    },
    [activeUser, playTrin, state],
  );

  const handleStoryPlayAnother = useCallback(
    (rounds: RoundResult[], startedAt: string) => {
      persistStoryLesson(rounds, startedAt);
      setStorySessionKey((k) => k + 1);
      handleStartStory();
    },
    [persistStoryLesson, handleStartStory],
  );

  const persistStory2Lesson = useCallback(
    (rounds: RoundResult[], startedAt: string) => {
      if (!activeUser || !playTrin) return;
      const result = aggregateRounds(
        playTrin.id,
        playTrin.tierId,
        startedAt,
        rounds,
      );
      const before = activeUser;
      let after = addLessonResult(before, result, {
        skipProgression: true,
      });
      const story2Round = playTrin.rounds[0]?.presentation;
      const seedId =
        story2Round?.kind === 'story2' ? story2Round.seedId : undefined;
      if (seedId) {
        after = {
          ...after,
          preferences: {
            ...(after.preferences ?? defaultPreferences()),
            lastStory2SeedId: seedId,
          },
        };
      }
      const beforeIds = new Set(before.badges.map((b) => b.id));
      const fresh = after.badges.filter((b) => !beforeIds.has(b.id));
      setState(upsertUser(state, after));
      setNewBadges(fresh);
      setWarmupTrin(null);
      setLessonStartRoundIndex(null);
    },
    [activeUser, playTrin, state],
  );

  const handleStory2PlayAnother = useCallback(
    (rounds: RoundResult[], startedAt: string) => {
      persistStory2Lesson(rounds, startedAt);
      setStory2SessionKey((k) => k + 1);
      handleStartStory2();
    },
    [persistStory2Lesson, handleStartStory2],
  );

  const handleStartBonusTal = useCallback(() => {
    if (!activeUser) return;
    const trin = buildBonusTalTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartBonusEmail = useCallback(() => {
    if (!activeUser) return;
    const trin = buildBonusEmailTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartWeakSpots = useCallback(() => {
    if (!activeUser) return;
    const trin = buildWeakSpotsTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartMentalImagery = useCallback(() => {
    if (!activeUser) return;
    const trin = buildMentalImageryTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartPunctuationPractice = useCallback(() => {
    if (!activeUser) return;
    const trin = buildPunctuationPracticeTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartCustomPractice = useCallback(() => {
    if (!activeUser) return;
    const trin = buildCustomPracticeShellTrin(activeUser);
    if (!trin) return;
    setWarmupTrin(trin);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setPlayCheckpointId(null);
    setLastResult(null);
    setLastSprint(null);
    setNewBadges([]);
    setScreen('lesson');
  }, [activeUser]);

  const handleStartCheckpoint = useCallback(
    (checkpointId: string) => {
      if (!activeUser) return;
      const cp = getCheckpoint(checkpointId);
      if (
        !cp ||
        !canStartCheckpoint(cp) ||
        !isCheckpointAccessible(activeUser, cp)
      ) {
        return;
      }
      setPlayCheckpointId(checkpointId);
      setLessonTrinId(null);
      setLessonStartRoundIndex(null);
      setLastResult(null);
      setLastSprint(null);
      setNewBadges([]);
      setSprintSessionKey((k) => k + 1);
      setScreen('checkpoint');
    },
    [activeUser],
  );

  const handleStartLesson = () => {
    if (!activeUser) return;
    const trin = getActiveTrin(activeUser);
    if (!canStartTrin(trin)) return;
    handleStartTrin(trin.id);
  };

  const handleLessonFinish = (
    rounds: RoundResult[],
    startedAt: string,
  ) => {
    if (!activeUser || !playTrin) return;
    if (rounds.length === 0) {
      setWarmupTrin(null);
      setLessonStartRoundIndex(null);
      setScreen('dashboard');
      return;
    }
    const isExtra = isExtraTrinId(playTrin.id);
    const result = aggregateRounds(
      playTrin.id,
      playTrin.tierId,
      startedAt,
      rounds,
    );
    const prev = isExtra ? null : getTrinRecord(activeUser, playTrin.id);
    const beatRecord =
      !isExtra &&
      (prev?.attempts ?? 0) > 0 &&
      (result.wpm > (prev?.bestWpm ?? 0) ||
        result.accuracy > (prev?.bestAccuracy ?? 0));
    const before = activeUser;
    let after = addLessonResult(before, result, {
      skipProgression: isExtra,
    });
    if (result.trinId === STORY_TRIN_ID) {
      const storyRound = playTrin.rounds[0]?.presentation;
      const storyId =
        storyRound?.kind === 'story' ? storyRound.storyId : undefined;
      if (storyId) {
        after = {
          ...after,
          preferences: {
            ...(after.preferences ?? defaultPreferences()),
            lastStoryId: storyId,
          },
        };
      }
    }
    if (result.trinId === STORY2_TRIN_ID) {
      const story2Round = playTrin.rounds[0]?.presentation;
      const seedId =
        story2Round?.kind === 'story2' ? story2Round.seedId : undefined;
      if (seedId) {
        after = {
          ...after,
          preferences: {
            ...(after.preferences ?? defaultPreferences()),
            lastStory2SeedId: seedId,
          },
        };
      }
    }
    const survivalRound = result.rounds.find(
      (r) => r.metronomeMode === 'survival' && r.survivalMs != null,
    );
    let survivalRecordBeat = false;
    if (survivalRound?.survivalMs != null) {
      const survivalUpdate = updateMetronomeSurvivalRecord(
        after,
        survivalRound.survivalMs,
      );
      after = survivalUpdate.user;
      survivalRecordBeat = survivalUpdate.isNewRecord;
    }
    const beforeIds = new Set(before.badges.map((b) => b.id));
    const fresh = after.badges.filter((b) => !beforeIds.has(b.id));
    setState(upsertUser(state, after));
    setNewBadges(fresh);
    setWarmupTrin(null);
    setLessonStartRoundIndex(null);

    if (result.trinId === STORY_TRIN_ID || result.trinId === STORY2_TRIN_ID) {
      setProofTrinId(null);
      setScreen('dashboard');
      return;
    }

    setLastResult(result);
    setLessonRecordBeat(beatRecord);
    setMetronomeSurvivalRecordBeat(survivalRecordBeat);

    const cleared = result.accuracy >= 0.95 && result.passedAllRounds;
    const showProof =
      PROGRESSION.trinProof &&
      !isExtra &&
      cleared &&
      !hasPassedTrinProof(after, result.trinId);

    if (showProof) {
      setProofTrinId(result.trinId);
      setProofFailed(false);
      setLastProofAccuracy(null);
      setScreen('trinProof');
    } else {
      setProofTrinId(null);
      setScreen('summary');
    }
  };

  const handleTrinProofFinish = (
    round: RoundResult,
    passed: boolean,
  ) => {
    if (!activeUser || !proofTrin) return;
    const proof = {
      trinId: proofTrin.id,
      startedAt: new Date(Date.now() - round.durationMs).toISOString(),
      durationMs: round.durationMs,
      accuracy: round.accuracy,
      wpm: round.wpm,
      passed,
      correctChars: round.correctChars,
      totalKeystrokes: round.totalKeystrokes,
    };
    const before = activeUser;
    const after = addTrinProofResult(before, proof);
    const beforeIds = new Set(before.badges.map((b) => b.id));
    const fresh = after.badges.filter((b) => !beforeIds.has(b.id));
    setState(upsertUser(state, after));
    setNewBadges((prev) => [...prev, ...fresh]);
    setProofFailed(!passed);
    if (passed) {
      setLastProofAccuracy(round.accuracy);
      setScreen('trinPassed');
    } else {
      setScreen('summary');
    }
  };

  const handleTrinPassedContinue = () => {
    setScreen('summary');
  };

  const handleRetryProof = () => {
    if (!lastResult) return;
    setProofFailed(false);
    setProofTrinId(lastResult.trinId);
    setScreen('trinProof');
  };

  const handleStartMiniCheckpoint = useCallback(
    (checkpointId: string) => {
      if (!activeUser) return;
      const cp = getMiniCheckpoint(checkpointId);
      if (
        !cp ||
        !canStartMiniCheckpoint(cp) ||
        !isMiniCheckpointAccessible(activeUser, cp)
      ) {
        return;
      }
      setPlayMiniCheckpointId(checkpointId);
      setPlayCheckpointId(null);
      setLessonTrinId(null);
      setLastMiniCheckpoint(null);
      setNewBadges([]);
      setMiniCheckpointSessionKey((k) => k + 1);
      setScreen('miniCheckpoint');
    },
    [activeUser],
  );

  const handleMiniCheckpointFinish = (payload: {
    wordsCompleted: number;
    wordsSkipped: number;
    startedAt: string;
    accuracy: number;
    passed: boolean;
  }) => {
    if (!activeUser || !playMiniCheckpoint) return;
    const result: MiniCheckpointResult = {
      checkpointId: playMiniCheckpoint.id,
      afterTrinId: playMiniCheckpoint.afterTrinId,
      tierId: playMiniCheckpoint.tierId,
      startedAt: payload.startedAt,
      wordsCompleted: payload.wordsCompleted,
      wordsSkipped: payload.wordsSkipped,
      durationMs: playMiniCheckpoint.durationMs,
      accuracy: payload.accuracy,
      passed: payload.passed,
    };
    const after = addMiniCheckpointResult(activeUser, result);
    setState(upsertUser(state, after));
    setLastMiniCheckpoint(result);
    setScreen('miniCheckpointSummary');
  };

  const handleCheckpointFinish = (
    wordsCompleted: number,
    wordsSkipped: number,
    startedAt: string,
  ) => {
    if (!activeUser || !playCheckpoint) return;
    const { user: after, isNewRecord } = addCheckpointResult(
      activeUser,
      playCheckpoint.id,
      wordsCompleted,
      startedAt,
    );
    const beforeIds = new Set(activeUser.badges.map((b) => b.id));
    const fresh = after.badges.filter((b) => !beforeIds.has(b.id));
    setState(upsertUser(state, after));
    setLastSprint({
      checkpointId: playCheckpoint.id,
      afterTierId: playCheckpoint.afterTierId,
      beforeTierId: playCheckpoint.beforeTierId,
      startedAt,
      wordsCompleted,
      wordsSkipped,
      durationMs: playCheckpoint.sprintDurationMs ?? 60_000,
      isNewRecord,
    });
    setNewBadges(fresh);
    setScreen('sprintSummary');
  };

  const handleRepeat = () => {
    if (lastMiniCheckpoint) {
      setPlayMiniCheckpointId(lastMiniCheckpoint.checkpointId);
      setLastMiniCheckpoint(null);
      setMiniCheckpointSessionKey((k) => k + 1);
      setScreen('miniCheckpoint');
      return;
    }
    if (lastSprint) {
      setPlayCheckpointId(lastSprint.checkpointId);
      setLastSprint(null);
      setNewBadges([]);
      setSprintSessionKey((k) => k + 1);
      setScreen('checkpoint');
      return;
    }
    if (!lastResult) return;
    if (
      proofFailed &&
      PROGRESSION.trinProof &&
      !isExtraTrinId(lastResult.trinId)
    ) {
      handleRetryProof();
      return;
    }
    if (lastResult.trinId === WARMUP_TRIN_ID) {
      setLastResult(null);
      setNewBadges([]);
      handleStartWarmup();
      return;
    }
    if (isExtraTrinId(lastResult.trinId)) {
      setLastResult(null);
      setNewBadges([]);
      // Re-create the same extra round (metronome / shadow) from id.
      if (lastResult.trinId === METRONOME_TRIN_ID) handleStartMetronome();
      else if (lastResult.trinId === SHADOW_TRIN_ID) handleStartShadow();
      else if (lastResult.trinId === DICTATION_TRIN_ID) handleStartDictation();
      else if (lastResult.trinId === STORY_TRIN_ID) handleStartStory();
      else if (lastResult.trinId === STORY2_TRIN_ID) handleStartStory2();
      else if (lastResult.trinId === WEAK_SPOTS_TRIN_ID) handleStartWeakSpots();
      else if (lastResult.trinId === BONUS_TAL_TRIN_ID) handleStartBonusTal();
      else if (lastResult.trinId === BONUS_EMAIL_TRIN_ID) handleStartBonusEmail();
      else if (lastResult.trinId === PUNCTUATION_PRACTICE_TRIN_ID)
        handleStartPunctuationPractice();
      else if (lastResult.trinId === CUSTOM_PRACTICE_TRIN_ID)
        handleStartCustomPractice();
      else if (lastResult.trinId === MENTAL_IMAGERY_TRIN_ID)
        handleStartMentalImagery();
      else setScreen('dashboard');
      return;
    }
    setLastResult(null);
    setLastSprint(null);
    setLessonStartRoundIndex(null);
    setNewBadges([]);
    setScreen('lesson');
  };

  const handleAdvance = () => {
    if (!activeUser || !lastResult) return;
    if (
      PROGRESSION.requireTrinProof &&
      !isExtraTrinId(lastResult.trinId) &&
      !hasPassedTrinProof(activeUser, lastResult.trinId)
    ) {
      return;
    }
    const next =
      getNextTrin(lastResult.trinId) ?? getActiveTrin(activeUser);
    setLastResult(null);
    setLessonRecordBeat(false);
    setMetronomeSurvivalRecordBeat(false);
    setNewBadges([]);
    setLessonTrinId(next.id);
    setLessonStartRoundIndex(null);
    setScreen(canStartTrin(next) ? 'lesson' : 'dashboard');
  };

  const handleSprintContinue = () => {
    if (!activeUser || !lastSprint) return;
    const after = advanceAfterCheckpoint(activeUser, lastSprint.checkpointId);
    setState(upsertUser(state, after));
    setLastSprint(null);
    setNewBadges([]);
    setPlayCheckpointId(null);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setScreen('dashboard');
  };

  const handleBackToDashboard = () => {
    setLastResult(null);
    setLastSprint(null);
    setLessonTrinId(null);
    setLessonStartRoundIndex(null);
    setWarmupTrin(null);
    setPlayCheckpointId(null);
    setPlayMiniCheckpointId(null);
    setLastMiniCheckpoint(null);
    setProofTrinId(null);
    setLastProofAccuracy(null);
    setProofFailed(false);
    setLessonRecordBeat(false);
    setMetronomeSurvivalRecordBeat(false);
    setNewBadges([]);
    setBaselineRetest(null);
    if (activeUser && needsOnboarding(activeUser)) {
      setOnboardingPhase(resumeOnboardingPhase(activeUser));
      setScreen('onboarding');
      return;
    }
    setScreen('dashboard');
  };

  const handleCancelBaseline = () => {
    if (activeUser && needsBaselineTranscription(activeUser)) {
      return;
    }
    setBaselineRetest(null);
    if (activeUser && needsOnboarding(activeUser)) {
      setOnboardingPhase(
        activeUser.baselineTranscription ? 'offerEveryday' : 'welcome',
      );
      setScreen('onboarding');
      return;
    }
    setScreen('dashboard');
  };

  const shellProps = {
    theme: state.theme,
    onToggleTheme: handleToggleTheme,
    onLogoClick: handleGoHome,
    saveError,
    onRetrySave: retrySave,
    onDismissSaveError: () => setSaveError(false),
  };

  if (!hydrated) {
    return (
      <Shell {...shellProps}>
        <p className="text-faint">Indlæser…</p>
      </Shell>
    );
  }

  if (screen === 'home') {
    return (
      <Shell {...shellProps}>
        <HomePage
          hasUsers={state.users.length > 0}
          hasActiveUser={activeUser !== null}
          authenticated={authenticated}
          onStart={handleHomeStart}
          onLogin={() => setAuthOpen(true)}
          onLogout={() => void handleLogout()}
          onPrivacy={() => openLegal('home')}
        />
        {authOpen && (
          <AuthModal
            onClose={() => setAuthOpen(false)}
            onSuccess={() => void handleAuthSuccess()}
            onLogin={(e, p) => persistence.login(e, p)}
            onRegister={(e, p) => persistence.register(e, p)}
          />
        )}
      </Shell>
    );
  }

  if (screen === 'privacy') {
    return (
      <Shell {...shellProps}>
        <LegalPage onBack={() => setScreen(legalReturnScreen.current)} />
      </Shell>
    );
  }

  if (screen === 'users' || !activeUser) {
    return (
      <Shell {...shellProps}>
        <UserSwitcher
          users={state.users}
          activeUserId={state.activeUserId}
          authenticated={authenticated}
          onSelect={handleSelectUser}
          onCreate={handleCreateUser}
          onDelete={handleDeleteUser}
          onOpenAuth={() => setAuthOpen(true)}
          onLogout={() => void handleLogout()}
          onDeleteAccount={() => setDeleteAccountOpen(true)}
          onPrivacy={() => openLegal('users')}
          onCancel={
            activeUser && state.users.length > 0
              ? () => navigateForUser(activeUser)
              : state.users.length > 0
                ? () => setScreen('home')
                : undefined
          }
        />
        {authOpen && (
          <AuthModal
            onClose={() => setAuthOpen(false)}
            onSuccess={() => void handleAuthSuccess()}
            onLogin={(e, p) => persistence.login(e, p)}
            onRegister={(e, p) => persistence.register(e, p)}
          />
        )}
        {deleteAccountOpen && (
          <DeleteAccountModal
            onClose={() => setDeleteAccountOpen(false)}
            onConfirm={(password) => handleDeleteAccount(password)}
          />
        )}
      </Shell>
    );
  }

  if (screen === 'lesson' && playTrin) {
    if (
      !canStartTrin(playTrin) &&
      playTrin.id !== CUSTOM_PRACTICE_TRIN_ID &&
      playTrin.id !== FLOW_PRACTICE_TRIN_ID
    ) {
      return (
        <Shell {...shellProps}>
          <div className="surface mx-auto max-w-lg p-6 text-center">
            <p className="text-strong">Ingen trin tilgængelige endnu</p>
            <p className="mt-2 text-muted">
              Dette makro-niveau er under udvikling. Vælg et tidligere trin på
              oversigten.
            </p>
            <button
              onClick={() => setScreen('dashboard')}
              className="btn-primary mt-4"
            >
              Tilbage
            </button>
          </div>
        </Shell>
      );
    }

    return (
      <Shell {...shellProps}>
        <LessonScreen
          key={`${playTrin.id}:${lessonStartRoundIndex ?? 'full'}:s${storySessionKey}:s2${story2SessionKey}`}
          trin={playTrin}
          user={activeUser}
          onFinish={handleLessonFinish}
          onStoryPlayAnother={
            playTrin.id === STORY_TRIN_ID
              ? handleStoryPlayAnother
              : playTrin.id === STORY2_TRIN_ID
                ? handleStory2PlayAnother
                : undefined
          }
          onCancel={handleBackToDashboard}
          onPreferencesChange={handlePreferencesChange}
          devStartRoundIndex={
            import.meta.env.DEV && lessonStartRoundIndex !== null
              ? lessonStartRoundIndex
              : undefined
          }
        />
      </Shell>
    );
  }

  if (screen === 'trinProof' && proofTrin) {
    return (
      <Shell {...shellProps}>
        <TrinProofScreen
          key={proofTrin.id}
          trin={proofTrin}
          user={activeUser}
          onFinish={handleTrinProofFinish}
          onCancel={handleBackToDashboard}
        />
      </Shell>
    );
  }

  if (screen === 'trinPassed' && proofTrin && lastProofAccuracy !== null) {
    return (
      <Shell {...shellProps}>
        <TrinPassedScreen
          trin={proofTrin}
          proofAccuracy={lastProofAccuracy}
          onContinue={handleTrinPassedContinue}
        />
      </Shell>
    );
  }

  if (screen === 'summary' && lastResult) {
    return (
      <Shell {...shellProps}>
        <LessonSummary
          result={lastResult}
          user={activeUser}
          newBadges={newBadges}
          recordBeat={lessonRecordBeat}
          survivalRecordBeat={metronomeSurvivalRecordBeat}
          proofFailed={proofFailed}
          onRepeat={handleRepeat}
          onRetryProof={
            PROGRESSION.trinProof ? handleRetryProof : undefined
          }
          onAdvance={handleAdvance}
          onBack={handleBackToDashboard}
        />
      </Shell>
    );
  }

  if (screen === 'miniCheckpoint' && playMiniCheckpoint) {
    return (
      <Shell {...shellProps}>
        <MiniCheckpointScreen
          key={miniCheckpointSessionKey}
          checkpoint={playMiniCheckpoint}
          keyboardLayout={activeUser.keyboardLayout}
          audioEnabled={activeUser.preferences?.audioEnabled !== false}
          onFinish={handleMiniCheckpointFinish}
          onCancel={handleBackToDashboard}
        />
      </Shell>
    );
  }

  const summaryMiniCheckpoint = lastMiniCheckpoint
    ? getMiniCheckpoint(lastMiniCheckpoint.checkpointId)
    : null;

  if (
    screen === 'miniCheckpointSummary' &&
    lastMiniCheckpoint &&
    summaryMiniCheckpoint
  ) {
    return (
      <Shell {...shellProps}>
        <MiniCheckpointSummary
          result={lastMiniCheckpoint}
          checkpoint={summaryMiniCheckpoint}
          onRepeat={handleRepeat}
          onBack={handleBackToDashboard}
        />
      </Shell>
    );
  }

  if (screen === 'checkpoint' && playCheckpoint) {
    return (
      <Shell {...shellProps}>
        <WordSprintScreen
          key={sprintSessionKey}
          checkpoint={playCheckpoint}
          keyboardLayout={activeUser.keyboardLayout}
          audioEnabled={activeUser.preferences?.audioEnabled !== false}
          onFinish={handleCheckpointFinish}
          onCancel={handleBackToDashboard}
        />
      </Shell>
    );
  }

  const summaryCheckpoint = lastSprint
    ? getCheckpoint(lastSprint.checkpointId)
    : null;

  if (screen === 'sprintSummary' && lastSprint && summaryCheckpoint) {
    return (
      <Shell {...shellProps}>
        <SprintSummary
          result={lastSprint}
          checkpoint={summaryCheckpoint}
          user={activeUser}
          onRepeat={handleRepeat}
          onContinue={handleSprintContinue}
          onBack={handleBackToDashboard}
        />
      </Shell>
    );
  }

  if (screen === 'onboarding' && activeUser) {
    return (
      <Shell {...shellProps}>
        <OnboardingFlow
          user={activeUser}
          phase={onboardingPhase}
          onStartTranscription={handleStartOnboardingBaseline}
          onStartComposition={() => {
            setBaselineRetest(null);
            setScreen('baselineComposition');
          }}
          onSkipComposition={() => setOnboardingPhase('summary')}
          onContinueToGoals={() => setOnboardingPhase('goals')}
          onCompleteGoals={handleCompleteOnboarding}
        />
      </Shell>
    );
  }

  if (screen === 'baselineTranscription' && activeUser) {
    const baselineRequired = needsBaselineTranscription(activeUser);
    return (
      <Shell {...shellProps}>
        <BaselineTranscriptionScreen
          keyboardLayout={activeUser.keyboardLayout}
          passageSeed={activeUser.id.length}
          onFinish={handleBaselineTranscriptionFinish}
          onCancel={baselineRequired ? undefined : handleCancelBaseline}
          required={baselineRequired}
        />
      </Shell>
    );
  }

  if (screen === 'baselineComposition' && activeUser) {
    return (
      <Shell {...shellProps}>
        <BaselineCompositionScreen
          keyboardLayout={activeUser.keyboardLayout}
          promptSeed={activeUser.id.length + 1}
          onFinish={handleBaselineCompositionFinish}
          onCancel={handleCancelBaseline}
        />
      </Shell>
    );
  }

  if (screen === 'skriveleg' && activeUser) {
    return (
      <Shell {...shellProps}>
        <SkrivelegScreen
          keyboardLayout={activeUser.keyboardLayout}
          initialGame={activeUser.preferences?.lastSkrivelegGame}
          initialMysteryVariant={activeUser.preferences?.lastMysteryVariant}
          initialGuessCategory={activeUser.preferences?.lastGuessCategoryId}
          onPreferencesChange={handlePreferencesChange}
          onFinish={handleFinishSkriveleg}
          onCancel={handleFinishSkriveleg}
        />
      </Shell>
    );
  }

  if (screen === 'story3' && activeUser) {
    return (
      <Shell {...shellProps}>
        <Story3Screen
          keyboardLayout={activeUser.keyboardLayout}
          promptSeed={activeUser.id.length + 3}
          initialMode={activeUser.preferences?.lastStory3FeedbackMode}
          initialExpert={activeUser.preferences?.lastStory3ExpertId}
          initialCustomExpert={activeUser.preferences?.lastStory3CustomExpert}
          initialNotebook={activeUser.preferences?.story3Notebook}
          onPreferencesChange={handlePreferencesChange}
          onFinish={handleFinishStory3}
          onCancel={handleFinishStory3}
        />
      </Shell>
    );
  }

  if (screen === 'wpmCheckIn' && activeUser) {
    return (
      <Shell {...shellProps}>
        <WpmCheckInScreen
          user={activeUser}
          keyboardLayout={activeUser.keyboardLayout}
          textSeed={Date.now()}
          onFinish={handleWpmCheckInFinish}
          onCancel={handleCancelWpmCheckIn}
        />
      </Shell>
    );
  }

  if (screen === 'wpmCheckInSummary' && wpmCheckInSummary) {
    return (
      <Shell {...shellProps}>
        <WpmCheckInSummary
          checkIn={wpmCheckInSummary.checkIn}
          previous={wpmCheckInSummary.previous}
          baseline={wpmCheckInSummary.baseline}
          onContinue={handleWpmCheckInSummaryContinue}
        />
      </Shell>
    );
  }

  return (
    <Shell {...shellProps}>
      <Dashboard
        user={activeUser}
        onStartLesson={handleStartLesson}
        onStartTrin={handleStartTrin}
        onStartCheckpoint={handleStartCheckpoint}
        onStartMiniCheckpoint={handleStartMiniCheckpoint}
        onStartWarmup={handleStartWarmup}
        onStartFlowPractice={handleStartFlowPractice}
        onStartMetronome={handleStartMetronome}
        onStartShadow={handleStartShadow}
        onStartDictation={handleStartDictation}
        onStartStory={handleStartStory}
        onStartStory2={handleStartStory2}
        onStartSkriveleg={handleStartSkriveleg}
        onStartStory3={handleStartStory3}
        onStartWeakSpots={handleStartWeakSpots}
        onStartMentalImagery={handleStartMentalImagery}
        onStartPunctuationPractice={handleStartPunctuationPractice}
        onStartCustomPractice={handleStartCustomPractice}
        onStartBonusTal={handleStartBonusTal}
        onStartBonusEmail={handleStartBonusEmail}
        onSwitchUser={() => setScreen('users')}
        onKeyboardLayoutChange={handleKeyboardLayout}
        onPreferencesChange={handlePreferencesChange}
        onGoalsChange={handleGoalsChange}
        onRetestBaseline={handleRetestBaseline}
        onStartWpmCheckIn={handleStartWpmCheckIn}
        onStartOnboarding={handleStartOnboardingBaseline}
        onDevJump={import.meta.env.DEV ? handleDevJump : undefined}
        onDevJumpAndStart={
          import.meta.env.DEV ? handleDevJumpAndStart : undefined
        }
        onDevJumpToDictation={
          import.meta.env.DEV ? handleDevJumpToDictation : undefined
        }
      />
    </Shell>
  );
}

function Shell({
  theme,
  onToggleTheme,
  onLogoClick,
  saveError,
  onRetrySave,
  onDismissSaveError,
  children,
}: {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onLogoClick: () => void;
  saveError?: boolean;
  onRetrySave?: () => void;
  onDismissSaveError?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen app-bg py-10 select-none">
      <div className={`mx-auto max-w-6xl px-6 ${saveError ? 'pb-24' : ''}`}>
        <div className="mb-8 flex items-center justify-between">
          <button
            type="button"
            onClick={onLogoClick}
            aria-label="Gå til forsiden"
            className="flex items-center gap-2 rounded-lg transition hover:opacity-80"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-500 shadow-[0_0_12px_rgba(99,102,241,0.5)] dark:bg-indigo-400 dark:shadow-[0_0_12px_rgba(99,102,241,0.7)]"
              aria-hidden
            />
            <span className="font-semibold tracking-wide text-strong">
              TouchType
            </span>
          </button>
          <div className="flex items-center gap-3">
            <p className="hidden text-xs text-faint sm:block">
              Ti-fingersystemet · øv dansk touchtyping
            </p>
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          </div>
        </div>
        {children}
      </div>
      {saveError && onRetrySave && onDismissSaveError && (
        <SaveErrorBanner onRetry={onRetrySave} onDismiss={onDismissSaveError} />
      )}
    </div>
  );
}
